import { useEffect, useMemo, useState } from 'react';
import type { GalleryItem } from '../data/galleryData';

type ImageLoadState = 'loading' | 'loaded' | 'error';

const paletteByTool: Record<GalleryItem['toolType'], { base: string; glow: string; icon: string }> = {
  text2img: { base: '#FCE7F3', glow: '#A855F7', icon: 'ri-quill-pen-ai-line' },
  img2img: { base: '#FEF3C7', glow: '#F59E0B', icon: 'ri-image-edit-line' },
  edit: { base: '#DCFCE7', glow: '#10B981', icon: 'ri-magic-line' },
  restore: { base: '#E0E7FF', glow: '#6366F1', icon: 'ri-hd-line' },
  tryon: { base: '#FCE7F3', glow: '#EC4899', icon: 'ri-shirt-line' },
};

const galleryImageStateCache = new Map<string, Exclude<ImageLoadState, 'loading'>>();
const galleryImagePreloadCache = new Map<string, Promise<Exclude<ImageLoadState, 'loading'>>>();

function getCardImageUrl(src: string, width: number, ratio: string) {
  try {
    const url = new URL(src);
    if (url.hostname === 'readdy.ai' && url.pathname === '/api/search-image') {
      return src;
    }
    const height = ratio === '4:3' ? Math.round((width * 3) / 4) : ratio === '1:1' ? width : Math.round((width * 4) / 3);
    url.searchParams.set('width', String(width));
    url.searchParams.set('height', String(height));
    return url.toString();
  } catch {
    return src;
  }
}

function preloadGalleryImage(src: string) {
  const cachedState = galleryImageStateCache.get(src);
  if (cachedState) return Promise.resolve(cachedState);

  const cachedPreload = galleryImagePreloadCache.get(src);
  if (cachedPreload) return cachedPreload;

  const preload = new Promise<Exclude<ImageLoadState, 'loading'>>((resolve) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      galleryImageStateCache.set(src, 'loaded');
      resolve('loaded');
    };
    image.onerror = () => {
      galleryImageStateCache.set(src, 'error');
      resolve('error');
    };
    image.src = src;
  }).finally(() => {
    galleryImagePreloadCache.delete(src);
  });

  galleryImagePreloadCache.set(src, preload);
  return preload;
}

interface GalleryCardImageProps {
  item: GalleryItem;
  alt: string;
  className?: string;
  width?: number;
  eager?: boolean;
}

export default function GalleryCardImage({
  item,
  alt,
  className = '',
  width = 420,
  eager = false,
}: GalleryCardImageProps) {
  const palette = paletteByTool[item.toolType] || paletteByTool.text2img;
  const src = useMemo(() => getCardImageUrl(item.imageUrl, width, item.ratio), [item.imageUrl, item.ratio, width]);
  const [state, setState] = useState<ImageLoadState>(() => galleryImageStateCache.get(src) ?? 'loading');

  useEffect(() => {
    let active = true;
    const cachedState = galleryImageStateCache.get(src);

    if (cachedState) {
      setState(cachedState);
      return () => {
        active = false;
      };
    }

    setState('loading');
    preloadGalleryImage(src).then((nextState) => {
      if (active) setState(nextState);
    });

    return () => {
      active = false;
    };
  }, [src]);

  const handleLoad = () => {
    galleryImageStateCache.set(src, 'loaded');
    setState('loaded');
  };

  const handleError = () => {
    galleryImageStateCache.set(src, 'error');
    setState('error');
  };

  return (
    <>
      <div
        className={`absolute inset-0 overflow-hidden transition-opacity duration-300 ${
          state === 'loaded' ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          background: `linear-gradient(145deg, ${palette.base} 0%, #FFFFFF 45%, ${palette.glow}20 100%)`,
        }}
        aria-hidden="true"
      >
        <div
          className="absolute -top-8 -right-10 h-28 w-28 rounded-full blur-2xl"
          style={{ background: `${palette.glow}4D` }}
        />
        <div
          className="absolute bottom-8 left-5 h-20 w-20 rounded-full blur-2xl"
          style={{ background: `${palette.glow}30` }}
        />
        <div className="absolute inset-x-0 top-0 h-full gallery-card-shimmer" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-2xl text-xl ${
              state === 'error' ? 'bg-white/75 text-gray-400' : 'bg-white/70'
            }`}
            style={state === 'error' ? undefined : { color: palette.glow }}
          >
            <i className={state === 'error' ? 'ri-image-line' : palette.icon} />
          </div>
        </div>
      </div>
      <img
        src={src}
        alt={alt}
        className={`relative h-full w-full object-cover object-top transition-opacity duration-500 ${
          state === 'loaded' ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        loading={eager || state === 'loaded' ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager || state === 'loaded' ? 'high' : 'auto'}
        onLoad={handleLoad}
        onError={handleError}
      />
    </>
  );
}
